import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgoraService, AuthState, StorageService, WebSocketChannelUser, WebsocketClientService, WsCallStateType } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { BookingState } from '@shared/booking-feature';
import { MCAAuthState } from '@shared/data';
import { combineLatest, combineLatestWith, filter, first, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CallStatusType } from '../enum/call-status-type.enum';
import { CallData, DeviceInfo } from '../interface';
import { CallActions } from '../store';
import { MediaAdapterBase } from './media-adapter.base';

@Injectable({ providedIn: 'root' })
export class AgoraMediaAdapterService extends MediaAdapterBase {
    private readonly agoraService = inject(AgoraService);
    private readonly wsService = inject(WebsocketClientService);
    private readonly storageService = inject(StorageService);
    private readonly store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);
    readonly booking$ = this.store.select(BookingState.booking);
    guid!: string;

    constructor() {
        super();
    }

    setGuid() {
        this.booking$
            .pipe(
                first(),
                map(booking => booking?.prereservationId),
                filter((guid): guid is string => !!guid),
                tap(guid => {
                    this.guid = guid;
                    this.storageService.setChannelId(guid);
                }),
            )
            .subscribe();
    }

    initLocalMedia(): Observable<void> {
        this.storageService.setUserType('Patient');
        this.storageService.setChannelId(this.guid);

        this.setCallState(WsCallStateType.DEVICE_TEST);

        return from(this.agoraService.initLocalMedia(0, undefined, this.guid)).pipe(
            switchMap(() => {
                return combineLatest([
                    this.store.select(MCAAuthState.userInfo$).pipe(
                        filter(userInfo => !!userInfo?.id),
                        map(userInfo => userInfo?.id),
                    ),
                    this.store.select(AuthState.tokenData$).pipe(
                        filter(tokenData => !!tokenData?.value?.accessTokenV2),
                        map(tokenData => tokenData?.value?.accessTokenV2),
                    ),
                ]);
            }),
            first(),
            switchMap(([userId, accessToken]) => {
                return this.wsService.initWebSocket(this.guid, userId, accessToken);
            }),
            map(() => undefined),
        );
    }

    destroyLocalMedia() {
        this.agoraService.closeLocalStream();
    }

    getMicDevices() {
        return this.agoraService.getRecordingDevices();
    }

    getCameraDevices() {
        return this.agoraService.getCameras();
    }

    getAudioDevices() {
        return this.agoraService.getPlayoutDevices();
    }

    setMic(enabled: boolean): Observable<void> {
        return from(this.agoraService.toggleAudio(enabled));
    }

    setCamera(enabled: boolean): Observable<void> {
        return from(this.agoraService.toggleVideo(enabled));
    }

    setAudio(volume: number): Observable<void> {
        return from(this.agoraService.setAudioVolume(volume));
    }

    setMicDevice(device: Partial<DeviceInfo>): Observable<void> {
        return from(this.agoraService.switchAudio(device));
    }

    setCameraDevice(device: Partial<DeviceInfo>): Observable<void> {
        return from(this.agoraService.switchCamera(device));
    }

    setAudioDevice(device: Partial<DeviceInfo>): Observable<void> {
        return from(this.agoraService.switchPlayoutDevice(device));
    }

    startCall(callData: CallData): Observable<CallStatusType> {
        this.setCallState(WsCallStateType.CALL_STARTING);
        return this.store.select(MCAAuthState.userInfo$).pipe(
            filter(userInfo => !!userInfo?.id && !!userInfo?.firstName && !!userInfo?.lastName),
            first(),
            tap(userInfo => {
                const userId = userInfo?.id;
                const userName = `${userInfo?.firstName} ${userInfo?.lastName}`;

                this.wsService.sendSocketMessage(
                    callData.channel,
                    new WebSocketChannelUser().deserialize({
                        clientReady: true,
                        microphoneReady: true,
                        cameraReady: true,
                        onTerm: true,
                        userId: userId,
                        userName: userName,
                    }),
                );
            }),
            switchMap(() => this.wsService.getRemoteClient(callData.channel)),
            combineLatestWith(this.wsService.getCallState()),
            map(([remoteClient, callState]) => this.handleRemoteStatusChange(remoteClient, callState, callData)),
            takeUntilDestroyed(this.destroyRef),
            catchError(() => {
                return of(CallStatusType.CALL_ERROR);
            }),
        );
    }

    endCall(): Observable<void> {
        this.setCallState(WsCallStateType.NONE);
        this.store.dispatch(new CallActions.SetCallStatus(null));
        this.wsService.endCall(this.guid);

        return this.store.select(MCAAuthState.userInfo$).pipe(
            filter(userInfo => !!userInfo?.id && !!userInfo?.firstName && !!userInfo?.lastName),
            first(),
            tap(userInfo => {
                const userId = userInfo?.id;
                const userName = `${userInfo?.firstName} ${userInfo?.lastName}`;

                this.wsService.sendSocketMessage(
                    this.guid,
                    new WebSocketChannelUser().deserialize({
                        clientReady: false,
                        onTerm: false,
                        userId: userId,
                        userName: userName,
                    }),
                );

                this.wsService.closeSocket(this.guid);
                this.agoraService.leave();
            }),
            tap(() => {
                this.destroyLocalMedia();
            }),
            switchMap(() => of(void 0)),
        );
    }

    setCallState(state: WsCallStateType) {
        this.wsService.setCallState(state);
    }

    private handleRemoteStatusChange(remoteClient: WebSocketChannelUser, callState: WsCallStateType, callData: CallData): CallStatusType {
        if (!remoteClient) {
            return CallStatusType.WAITING_FOR_REMOTE;
        }
        if (remoteClient.clientReady && callState === WsCallStateType.CALL_STARTED) {
            return CallStatusType.CALL_STARTED;
        }
        if (remoteClient.clientReady && callState === WsCallStateType.CALL_STARTING) {
            try {
                this.agoraService.join(remoteClient, callData.channel, callData.contractorId, callData.token);
                return CallStatusType.CALL_STARTED;
            } catch (error) {
                console.error('[TRACE] Error while joining call', error);
                return CallStatusType.CALL_ERROR;
            }
        }
        if (remoteClient.videoModalOpened && callState === WsCallStateType.CALL_STARTING) {
            return CallStatusType.REMOTE_PREPARING;
        }
        if (!remoteClient.inCall && callState === WsCallStateType.CALL_STARTED) {
            return CallStatusType.REMOTE_LEFT;
        }
        if (remoteClient.clientReady && (remoteClient.cameraError || remoteClient.microphoneError)) {
            return CallStatusType.REMOTE_DEVICE_ISSUES;
        }
        if (remoteClient.clientReady && (remoteClient.cameraMuted || remoteClient.microphoneMuted)) {
            return CallStatusType.REMOTE_MUTED_DEVICES;
        }
        if (remoteClient.clientJoined && !remoteClient.clientReady) {
            return CallStatusType.REMOTE_CONNECTING;
        }
        return CallStatusType.CALL_STARTING;
    }
}
